import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../shared/services/apiClient';
import { hefestoModelsApi } from '../../datasources/services/hefestoModels.service';
import toast from 'react-hot-toast';
import { datasourcesApi } from '../../datasources/services/datasources.service';
import { queryKeys } from '../../../shared/services/queryKeys';
import type { DataSource } from '@/types';
import type { HefestoModel } from '../../datasources/services/hefestoModels.service';
import { useDashboardJobStore } from '../../../shared/state/dashboardJobStore';
import { isSucursalesSupported, markSucursalesUnsupported } from '../../../shared/services/sucursalesSupport';

// Professional Dashboard Creation Form
// Single-step process: Name + (DataSource OR HefestoModel) → 5 Professional Widgets
// No prompts, no complexity, enterprise-ready UX

type Sucursal = {
  id: string;
  name: string;
  empresaId: string;
};

type DashboardFormData = {
  name: string;
  sourceType: string;
  dataSourceId: string;
  hefestoModelId: string;
  sucursalId: string;
};

type Props = {
  onSave: (dashboard: any) => void;
  onClose: () => void;
  currentUser?: any;
};

const ProfessionalDashboardForm: React.FC<Props> = ({ onSave, onClose, currentUser }) => {
  const [form, setForm] = useState<DashboardFormData>(() => {
    // Intentar recuperar del sessionStorage
    const savedForm = sessionStorage.getItem('dashboardFormData');
    console.log('🔍 Loading form from sessionStorage:', savedForm);
    if (savedForm) {
      try {
        const parsed = JSON.parse(savedForm);
        console.log('✅ Loaded saved form:', parsed);
        return parsed;
      } catch (e) {
        console.error('❌ Error parsing saved form data:', e);
      }
    }
    console.log('📝 No saved form, using defaults');
    return {
      name: '',
      sourceType: '', // 'datasource' or 'hefesto'
      dataSourceId: '',
      hefestoModelId: '',
      sucursalId: '', // Optional
    };
  });
  
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [hefestoModels, setHefestoModels] = useState<HefestoModel[]>([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [sucursalesSupported, setSucursalesSupported] = useState(isSucursalesSupported());
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();
  const startJob = useDashboardJobStore((state) => state.startJob);

  // Guardar form en sessionStorage cuando cambie
  useEffect(() => {
    console.log('💾 Form changed, saving to sessionStorage:', form);
    sessionStorage.setItem('dashboardFormData', JSON.stringify(form));
  }, [form]);

  const datasourcesQuery = useQuery<DataSource[]>({
    queryKey: queryKeys.datasources,
    queryFn: datasourcesApi.getDataSources,
  });

  const hefestoModelsQuery = useQuery<{ models: HefestoModel[]; count: number }>({
    queryKey: queryKeys.hefestoModels,
    queryFn: hefestoModelsApi.listModels,
  });

  // Load Active Data Sources and Hefesto Models
  useEffect(() => {
    loadActiveDataSources();
    loadHefestoModels();
  }, [datasourcesQuery.data, hefestoModelsQuery.data]);

  useEffect(() => {
    loadSucursales();
  }, []);

  const loadActiveDataSources = async () => {
    try {
      setLoading(true);
      const response = datasourcesQuery.data ?? [];
      
      // Only show ACTIVE datasources that are ready for dashboards
      const activeSources = response.filter((ds: DataSource) => 
        ds.status === 'ACTIVE' && ds.analysis
      );
      
      setDataSources(activeSources);
      
    } catch (error: any) {
      console.error('Error loading data sources:', error);
      toast.error('Failed to load data sources');
    } finally {
      setLoading(false);
    }
  };

  const loadHefestoModels = async () => {
    try {
      const response = hefestoModelsQuery.data;
      setHefestoModels(response?.models ?? []);
    } catch (error: any) {
      console.error('Error loading Hefesto models:', error);
    }
  };

  const loadSucursales = async () => {
    if (!isSucursalesSupported()) {
      setSucursalesSupported(false);
      setSucursales([]);
      return;
    }

    try {
      const response = await api.get('/sucursales');
      setSucursales(response.data || []);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        markSucursalesUnsupported();
        setSucursalesSupported(false);
        setSucursales([]);
        return;
      }
      console.error('Error loading sucursales:', error);
    }
  };

  // Create Professional Dashboard
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 🚨 PROTECTION: Prevent double submission
    if (creating) {
      console.log('⚠️ Dashboard creation already in progress, ignoring duplicate submit');
      return;
    }
    
    if (!form.name.trim()) {
      toast.error('Dashboard name is required');
      return;
    }
    
    if (!form.sourceType) {
      toast.error('Please select a data source or Hefesto model');
      return;
    }

    try {
      setCreating(true);
      
      console.log('🔍 [DEBUG] Form state:', {
        name: form.name,
        sourceType: form.sourceType,
        dataSourceId: form.dataSourceId,
        hefestoModelId: form.hefestoModelId,
        sucursalId: form.sucursalId
      });
      
      const payload: any = {
        name: form.name.trim(),
        sucursalId: form.sucursalId || undefined,
      };

      if (form.sourceType === 'datasource') {
        payload.dataSourceId = form.dataSourceId;
        console.log('📊 Using DataSource:', form.dataSourceId);
      } else {
        payload.hefestoModelId = form.hefestoModelId;
        console.log('🔥 Using Hefesto Model:', form.hefestoModelId);
      }
      
      console.log('📤 Creating professional dashboard with payload:', JSON.stringify(payload, null, 2));
      
      // Single API call: Create dashboard with auto-generated widgets
      const response = await createDashboardMutation.mutateAsync(payload);
      
      console.log('Professional dashboard created:', response);

      if (response?.jobId) {
        startJob(response.jobId);
        toast.success('Dashboard en creación. Podés seguir el progreso en la lista.');
        onClose();
        return;
      }

      toast.success(`Dashboard "${form.name}" agregado a la lista`);

      // Keep form data in sessionStorage for quick creation of multiple dashboards
      // Users can manually clear fields if they want to change datasource

      onSave(response);
      
    } catch (error: any) {
      console.error('Error creating dashboard:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create dashboard';
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const createDashboardMutation = useMutation({
    mutationFn: async (payload: any) => {
      const response = await api.post('/dashboards?async=true', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
    },
  });

  const selectedDataSource = dataSources.find(ds => ds.id === form.dataSourceId);
  const selectedHefesto = hefestoModels.find(hm => hm.id === form.hefestoModelId);
  const hasOptions = dataSources.length > 0 || hefestoModels.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-xl p-8 w-full max-w-md mx-4 border border-slate-700 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-100">
            Create Professional Dashboard
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200 transition-colors"
            disabled={creating}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dashboard Name */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Dashboard Name *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Sales Overview, Financial Report..."
              className="w-full p-3 border border-slate-600 bg-slate-700 text-slate-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all"
              disabled={creating}
              required
            />
          </div>

          {/* Data Source or Hefesto Model Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Data Source *
            </label>
            <select
              value={form.sourceType ? `${form.sourceType}:${form.sourceType === 'datasource' ? form.dataSourceId : form.hefestoModelId}` : ''}
              onChange={(e) => {
                const value = e.target.value;
                console.log('🎯 Dropdown changed, value:', value);
                if (!value) {
                  setForm(prev => ({ ...prev, sourceType: '', dataSourceId: '', hefestoModelId: '' }));
                } else {
                  const [type, id] = value.split(':');
                  console.log('🎯 Parsed:', { type, id });
                  if (type === 'datasource') {
                    console.log('📊 Setting DataSource:', id);
                    setForm(prev => ({ ...prev, sourceType: 'datasource', dataSourceId: id, hefestoModelId: '' }));
                  } else {
                    console.log('🔥 Setting Hefesto Model:', id);
                    setForm(prev => ({ ...prev, sourceType: 'hefesto', dataSourceId: '', hefestoModelId: id }));
                  }
                }
              }}
              className="w-full p-3 border border-slate-600 bg-slate-700 text-slate-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all"
              disabled={creating || loading}
              required
            >
              <option value="">Select a data source...</option>
              
              {/* Hefesto Models */}
              {hefestoModels.length > 0 && (
                <optgroup label="🔥 Hefesto Models (Recommended)">
                  {hefestoModels.map((hm) => (
                    <option key={hm.id} value={`hefesto:${hm.id}`}>
                      ⭐ {hm.name} ({hm.type}) - {hm.confidenceLevel}
                    </option>
                  ))}
                </optgroup>
              )}
              
              {/* Regular Data Sources */}
              {dataSources.length > 0 && (
                <optgroup label="📊 Data Sources">
                  {dataSources.map((ds) => (
                    <option key={ds.id} value={`datasource:${ds.id}`}>
                      {ds.name} ({ds.type}) - ACTIVE
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            
            {/* Source Info */}
            {selectedHefesto && (
              <div className="mt-2 p-2 bg-purple-500/10 border border-purple-500/30 rounded text-xs">
                <div className="text-purple-400 font-semibold">
                  ⭐ Hefesto {selectedHefesto.type === 'MULTI_SOURCE' ? 'MULTI-SOURCE' : 'SINGLE-SOURCE'}
                </div>
                <div className="text-slate-300">
                  Confidence: {selectedHefesto.confidenceLevel} • Sources: {selectedHefesto.sourceIds.length}
                </div>
              </div>
            )}
            {selectedDataSource && (
              <div className="mt-2 text-xs text-sky-400">
                ✅ Analyzed • Ready for professional dashboards
              </div>
            )}
            {!hasOptions && !loading && (
              <div className="mt-2 text-xs text-amber-400">
                ⚠️ No data sources or Hefesto models available. Please create one first.
              </div>
            )}
          </div>

          {!sucursalesSupported && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs text-amber-200">
              ⚠️ Las sucursales no están disponibles en este entorno, por lo que el dashboard se creará sin sucursal.
            </div>
          )}

          {/* Sucursal Selection (Optional) */}
          {sucursalesSupported && sucursales.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Branch (Optional)
              </label>
              <select
                value={form.sucursalId}
                onChange={(e) => setForm(prev => ({ ...prev, sucursalId: e.target.value }))}
                className="w-full p-3 border border-slate-600 bg-slate-700 text-slate-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all"
                disabled={creating}
              >
                <option value="">All branches</option>
                {sucursales.map((sucursal) => (
                  <option key={sucursal.id} value={sucursal.id}>
                    {sucursal.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t border-slate-600">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-slate-200 transition-colors"
              disabled={creating}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating || !form.name.trim() || (!form.dataSourceId && !form.hefestoModelId) || !hasOptions}
              className="bg-sky-600 text-white px-6 py-2 rounded-lg hover:bg-sky-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {creating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Creating Dashboard...</span>
                </>
              ) : (
                <>
                  <span>🚀</span>
                  <span>Create Professional Dashboard</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfessionalDashboardForm;
